<template>
  <div class="client-manager-dashboard-inner">
    <!-- Dashboard Tab Content -->
    <template v-if="activeTab === 'dashboard'">
      <ClientManagerDashboardOverview :stats="stats" />
    </template>

    <!-- Clients Tab Content -->
    <template v-if="activeTab === 'clients' && !selectedClientId">
      <ManagerClientList
        :clients="clients"
        :loading="loadingClients"
        @refresh="loadClients"
        @view="viewClient"
        @toggle-status="toggleClientStatus"
      />
    </template>

    <!-- Client Details View -->
    <template v-if="activeTab === 'clients' && selectedClientId">
      <ClientDetailsView
        :clientId="selectedClientId"
        details-source="manager"
        @back="onBackFromClientDetails"
      />
    </template>

    <!-- Settings Tab Content -->
    <template v-if="activeTab === 'settings-profile'">
      <ManagerProfileUpdate
        :managerUser="managerUser"
        @updated="handleProfileUpdated"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import ManagerClientList from '../components/ManagerClientList.vue';
import ClientDetailsView from '@/components/ClientDetailsView.vue';
import ClientManagerDashboardOverview from '../components/ClientManagerDashboardOverview.vue';
import ManagerProfileUpdate from '../components/ManagerProfileUpdate.vue';

export default {
  name: 'ClientManagerDashboard',
  inject: {
    setManagerUserDisplay: { default: null }
  },
  components: {
    ManagerClientList,
    ClientDetailsView,
    ClientManagerDashboardOverview,
    ManagerProfileUpdate
  },
  data() {
    return {
      managerUser: null,
      stats: {
        totalClients: 0,
        activeClients: 0,
        totalCompanies: 0
      },
      clients: [],
      loadingClients: false,
      activeTab: 'dashboard',
      selectedClientId: null,
      errors: {}
    };
  },
  mounted() {
    this.hydrateManagerUser();
    this.loadData();
    this.syncFromRoute();
  },
  watch: {
    '$route.fullPath'() {
      this.syncFromRoute();
    }
  },
  methods: {
    hydrateManagerUser() {
      const raw = localStorage.getItem('managerUser');
      if (raw) {
        try {
          this.managerUser = JSON.parse(raw);
        } catch {
          this.managerUser = null;
        }
      }
    },
    syncFromRoute() {
      if (this.$route.name !== 'ClientManagerDashboard') {
        return;
      }
      const q = this.$route.query || {};
      let nextTab = 'dashboard';
      if (q.tab === 'clients') {
        nextTab = 'clients';
      } else if (q.tab === 'settings-profile') {
        nextTab = 'settings-profile';
      }

      const tabChanged = this.activeTab !== nextTab;
      this.activeTab = nextTab;

      if (q.tab === 'clients' && q.focusClient != null && q.focusClient !== '') {
        const id = parseInt(String(q.focusClient), 10);
        this.selectedClientId = Number.isNaN(id) ? null : id;
      } else {
        this.selectedClientId = null;
      }

      if (this.activeTab === 'clients') {
        this.loadClients();
      } else if (this.activeTab === 'dashboard' && tabChanged) {
        // Refresh overview when returning to dashboard so counts match My Clients
        this.loadStats();
      }
    },
    async loadData() {
      try {
        await this.loadStats();
      } catch (error) {
        console.error('Error loading data:', error);
        this.errors.general = 'Failed to load data. Please try again.';
      }
    },
    async loadStats() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/client-manager/stats`);
        this.stats = response.data;
      } catch (error) {
        console.error('Error loading stats:', error);
        this.stats = {
          totalClients: 0,
          activeClients: 0,
          totalCompanies: 0
        };
      }
    },
    async loadClients() {
      this.loadingClients = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/client-manager/clients`);
        this.clients = response.data;
      } catch (error) {
        console.error('Error loading clients:', error);
        this.errors.general = 'Failed to load clients. Please try again.';
      } finally {
        this.loadingClients = false;
      }
    },
    viewClient(client) {
      this.$router.push({
        name: 'ClientManagerDashboard',
        query: { tab: 'clients', focusClient: String(client.id) }
      });
    },
    onBackFromClientDetails() {
      this.$router.push({
        name: 'ClientManagerDashboard',
        query: { tab: 'clients' }
      });
    },
    async toggleClientStatus(client) {
      try {
        await axios.patch(`${process.env.VUE_APP_API_URL}/client-manager/clients/${client.id}`, {
          is_active: !client.is_active
        });
        await this.loadClients();
        await this.loadStats();
      } catch (error) {
        console.error('Error toggling client status:', error);
        alert('Failed to update client status. Please try again.');
      }
    },
    handleProfileUpdated(updatedManager) {
      this.managerUser = updatedManager;
      localStorage.setItem('managerUser', JSON.stringify(updatedManager));
      if (typeof this.setManagerUserDisplay === 'function') {
        this.setManagerUserDisplay(updatedManager);
      }
    }
  }
};
</script>

<style scoped>
.client-manager-dashboard-inner {
  width: 100%;
  min-height: 0;
}
</style>
